export const dataPrefix = "sit";

export const dataAttributes = {
  downloadLabel: `${dataPrefix}DownloadLabel`,
  downloadFilename: `${dataPrefix}DownloadFilename`,
  teaserLabel: `${dataPrefix}TeaserLabel`,
  teaserHeadline: `${dataPrefix}TeaserHeadline`,
  teaserTimeout: `${dataPrefix}TeaserTimeout`,
  teaserImpressed: `${dataPrefix}TeaserImpressed`,
  impressionType: `${dataPrefix}ImpressionType`,
  impressionLabel: `${dataPrefix}ImpressionLabel`,
  impressionHeadline: `${dataPrefix}ImpressionHeadline`,
  impressionTimeout: `${dataPrefix}ImpressionTimeout`,
  impressionImpressed: `${dataPrefix}ImpressionImpressed`,
  searchResultLabel: `${dataPrefix}SearchResultLabel`,
  searchValue: `${dataPrefix}SearchValue`,
  searchResult: `${dataPrefix}SearchResult`,
  searchResultLevel: `${dataPrefix}SearchResultLevel`,
  searchResultPosition: `${dataPrefix}SearchResultPosition`,
  searchResultPage: `${dataPrefix}SearchResultPage`,
  action: `${dataPrefix}Action`,
  primaryCategory: `${dataPrefix}PrimaryCategory`,
  ignoreLabel: `${dataPrefix}IgnoreLabel`,
};

const classesToIgnore: string[] = [];

export function ignoreTextOfElementsWith(...classNames: string[]) {
  classesToIgnore.push(...classNames);
}

export function getHrefFromTarget(target: HTMLElement) {
  let href = (target as HTMLAnchorElement).href ?? target.dataset.sitHref;
  if (href === undefined) {
    return undefined;
  }
  if (href === window.location.href + "#") {
    return "";
  }
  if (
    !href.startsWith("http") &&
    !(href.startsWith("mailto:") || href.startsWith("tel:"))
  ) {
    href = document.location.origin + href;
  }
  if (href.endsWith("/")) {
    return href.slice(0, -1);
  }
  return href;
}

export function getDataFromTargetOrThrow(target: HTMLElement, label: string) {
  const data = target.dataset[label];
  if (data === undefined) {
    throwError(toKebabCase(label), data, target);
  }
  return data;
}

export function tryGetDataFromTarget(target: HTMLElement, label: string) {
  return target.dataset[label]?.trim();
}

function isIgnoredForLabel(element: HTMLElement) {
  if (element.dataset?.[dataAttributes.ignoreLabel] !== undefined) {
    return true;
  }
  return classesToIgnore.some((ignoredClass) =>
    element.classList?.contains(ignoredClass),
  );
}

function isInvisibleStyleDeclaration(style: CSSStyleDeclaration) {
  return (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.opacity === "0"
  );
}

function isInvisibleContent(element: HTMLElement) {
  if (element.nodeType !== Node.ELEMENT_NODE) {
    return true;
  }
  const computedStyle = getComputedStyle(element);
  if (!computedStyle) {
    return true;
  }
  if (isIgnoredForLabel(element)) {
    return true;
  }
  return isInvisibleStyleDeclaration(computedStyle);
}

function isTextNode(element: HTMLElement) {
  return element.nodeType === Node.TEXT_NODE;
}

function isReferencedByInvisibleSlot(node: Node) {
  const slot = (node as Element).assignedSlot;
  if (slot) {
    const style = getComputedStyle(slot);
    return isInvisibleStyleDeclaration(style);
  }
  return false;
}

export function getVisibleInnerText(element: HTMLElement) {
  if (isReferencedByInvisibleSlot(element)) {
    return "";
  }
  if (isTextNode(element)) {
    return element.textContent;
  }
  if (isInvisibleContent(element)) {
    return "";
  }
  return findTextInChildren(element, getVisibleInnerText);
}

export function getInvisibleInnerText(element: HTMLElement) {
  if (isTextNode(element)) {
    return element.textContent;
  }
  return findTextInChildren(element, getInvisibleInnerText);
}

export function getAriaLabel(element: HTMLElement) {
  if (isTextNode(element)) {
    return null;
  }
  const ariaLabel = element.getAttribute?.("aria-label");
  if (ariaLabel) {
    return ariaLabel;
  }
  return findTextInChildren(element, getAriaLabel);
}

function isImageElement(element: any): element is HTMLImageElement {
  return element.tagName?.toLowerCase() === "img";
}

export function getAltText(element: HTMLElement) {
  if (isImageElement(element) && element.alt) {
    return element.alt;
  }
  return findTextInChildren(element, getAltText);
}

const ignoredTags = ["noscript", "style", "#comment", "#document-fragment"];

function isSlotElement(element: any): element is HTMLSlotElement {
  return typeof element.assignedNodes === "function";
}

function findTextInChildren(
  element: HTMLElement,
  callback: (el: HTMLElement) => string | null,
) {
  let nodes;
  if (isSlotElement(element)) {
    nodes = element.assignedNodes();
  } else {
    nodes = Array.from(element.childNodes);
  }

  const elements = nodes.filter(
    (node) => !ignoredTags.includes(node.nodeName.toLowerCase()),
  ) as HTMLElement[];
  return elements
    .map((element) => element.dataset?.sitReplaceLabelWith ?? callback(element))
    .join(" ")
    .trim()
    .replace(/\s+/g, " ");
}

function throwError(attribute: string, givenValue: any, element: HTMLElement) {
  let message;
  if (typeof givenValue === "string") {
    message = `The attribute \`${attribute}\` can not be empty on the following teaser: ${element.outerHTML}`;
  } else {
    message = `The attribute \`${attribute}\` is missing on the teaser: ${element.outerHTML}`;
  }
  throw new Error(message);
}

function toKebabCase(camelCase: string) {
  return camelCase.replace(/([A-Z])/g, (match) => `-${match.toLowerCase()}`);
}
